import { useState, useEffect } from "react";
import { 
  Search, MapPin, Building2, User, Filter, Package,
  ChevronLeft, ChevronRight, Send, CheckCircle2, AlertCircle,
  Loader2, Users, Mail, RefreshCw, FileCheck, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// US States for dropdown
const US_STATES = [
  { code: "", name: "All States" },
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }
];

// Establishment type options
const ESTABLISHMENT_TYPES = [
  { value: "all", label: "All Types" },
  { value: "manufacturer", label: "Manufacturer" },
  { value: "distributor", label: "Distributor" },
  { value: "contract", label: "Contract Manufacturer" },
  { value: "repackager", label: "Repackager/Relabeler" }
];

export default function SupplierRecruitmentPage() {
  // Search state
  const [searchParams, setSearchParams] = useState({
    state: "TX",
    city: "",
    zip_code: "",
    company_name: "",
    establishment_type: "all",
    limit: 25,
    skip: 0
  });
  
  // Results state
  const [results, setResults] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  
  // Selection state
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Stats state
  const [stats, setStats] = useState(null);
  
  // Invite state
  const [inviting, setInviting] = useState(false);

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/recruitment/suppliers/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to load stats:", error);
    }
  };

  const handleSearch = async (newSkip = 0) => {
    setLoading(true);
    setSearched(true);
    
    try {
      const response = await fetch(`${API_URL}/api/recruitment/suppliers/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...searchParams,
          skip: newSkip
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Search failed");
      }
      
      const data = await response.json();
      setResults(data.results || []);
      setTotalCount(data.total_count || 0);
      setSearchParams(prev => ({ ...prev, skip: newSkip }));
      setSelectedSuppliers([]);
      setSelectAll(false);
    } catch (error) {
      toast.error(error.message);
      setResults([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedSuppliers([]);
    } else {
      setSelectedSuppliers(results.map(r => r.fei_number));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectSupplier = (feiNumber) => {
    setSelectedSuppliers(prev => {
      if (prev.includes(feiNumber)) {
        return prev.filter(f => f !== feiNumber);
      }
      return [...prev, feiNumber];
    });
  };

  const handleInvite = async () => {
    if (selectedSuppliers.length === 0) {
      toast.error("Please select at least one supplier to invite");
      return;
    }
    
    setInviting(true);
    
    try {
      const suppliersToInvite = results.filter(r => 
        selectedSuppliers.includes(r.fei_number)
      );
      
      const response = await fetch(`${API_URL}/api/recruitment/suppliers/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contractors: suppliersToInvite  // API expects 'contractors' field
        })
      });
      
      if (!response.ok) {
        throw new Error("Failed to send invitations");
      }
      
      const data = await response.json();
      toast.success(`Invited ${data.invited_count} suppliers. ${data.skipped_count} already invited.`);
      setSelectedSuppliers([]);
      setSelectAll(false);
      loadStats();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setInviting(false);
    }
  };

  const totalPages = Math.ceil(totalCount / searchParams.limit);
  const currentPage = Math.floor(searchParams.skip / searchParams.limit) + 1;

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Package className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Medical Device Supplier Recruitment</h1>
              <p className="text-slate-600">
                Search FDA registered medical device manufacturers and distributors nationwide
              </p>
            </div>
          </div>
        </div>

        {/* Data Source Info */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Globe className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-900">Data Source: FDA openFDA</p>
              <p className="text-sm text-blue-700">
                Official FDA Medical Device Registration & Listing database. Includes all registered manufacturers, 
                distributors, repackagers, and relabelers across all 50 US states.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_invites}</p>
                  <p className="text-sm text-slate-500">Total Invites</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.by_status?.pending || 0}</p>
                  <p className="text-sm text-slate-500">Pending</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Send className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.by_status?.sent || 0}</p>
                  <p className="text-sm text-slate-500">Sent</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.by_status?.joined || 0}</p>
                  <p className="text-sm text-slate-500">Joined</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Form */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-5 h-5 text-slate-500" />
            <h2 className="text-lg font-semibold text-slate-900">Search Filters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            {/* State */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">State</label>
              <select
                value={searchParams.state}
                onChange={(e) => setSearchParams({ ...searchParams, state: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {US_STATES.map(state => (
                  <option key={state.code} value={state.code}>{state.name}</option>
                ))}
              </select>
            </div>
            
            {/* City */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">City</label>
              <Input
                placeholder="e.g., Houston"
                value={searchParams.city}
                onChange={(e) => setSearchParams({ ...searchParams, city: e.target.value })}
              />
            </div>
            
            {/* ZIP Code */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">ZIP Code</label>
              <Input
                placeholder="e.g., 77001"
                value={searchParams.zip_code}
                onChange={(e) => setSearchParams({ ...searchParams, zip_code: e.target.value })}
              />
            </div>
            
            {/* Company Name */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Company Name</label>
              <Input
                placeholder="e.g., Medical Corp"
                value={searchParams.company_name}
                onChange={(e) => setSearchParams({ ...searchParams, company_name: e.target.value })}
              />
            </div>
            
            {/* Establishment Type */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Business Type</label>
              <select
                value={searchParams.establishment_type}
                onChange={(e) => setSearchParams({ ...searchParams, establishment_type: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {ESTABLISHMENT_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Data from official FDA Medical Device Registration database
            </p>
            <Button 
              onClick={() => handleSearch(0)}
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Search Suppliers
            </Button>
          </div>
        </div>

        {/* Results Section */}
        {searched && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Results Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-900">
                  {totalCount.toLocaleString()} suppliers found
                </p>
                <p className="text-sm text-slate-500">Source: FDA openFDA</p>
              </div>
              
              <div className="flex items-center gap-3">
                {selectedSuppliers.length > 0 && (
                  <Button 
                    onClick={handleInvite}
                    disabled={inviting}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {inviting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Invite Selected ({selectedSuppliers.length})
                  </Button>
                )}
                <Button variant="outline" onClick={() => handleSearch(searchParams.skip)}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>

            {/* Results Table */}
            {results.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={handleSelectAll}
                          className="w-4 h-4 text-amber-600 rounded"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Company</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Location</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">FEI #</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Reg. Expires</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {results.map((supplier) => (
                      <tr 
                        key={supplier.fei_number}
                        className={`hover:bg-slate-50 transition-colors ${
                          selectedSuppliers.includes(supplier.fei_number) ? "bg-amber-50" : ""
                        }`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedSuppliers.includes(supplier.fei_number)}
                            onChange={() => handleSelectSupplier(supplier.fei_number)}
                            className="w-4 h-4 text-amber-600 rounded"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-900 text-sm">{supplier.company_name}</p>
                              {supplier.product_codes?.length > 0 && (
                                <p className="text-xs text-slate-500">
                                  Products: {supplier.product_codes.slice(0, 3).join(", ")}
                                  {supplier.product_codes.length > 3 && "..."}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                            <div className="text-sm text-slate-700">
                              <p>{supplier.city}, {supplier.state} {supplier.zip_code}</p>
                              {supplier.address && (
                                <p className="text-xs text-slate-500">{supplier.address}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {supplier.establishment_types?.slice(0, 2).map((type, i) => (
                              <span 
                                key={i}
                                className="inline-flex px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full"
                              >
                                {type.replace("Medical Device", "").replace("Manufacture ", "Mfg ").trim().slice(0, 25)}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileCheck className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-700 font-mono">{supplier.fei_number}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            supplier.reg_expiry_year && parseInt(supplier.reg_expiry_year) >= new Date().getFullYear()
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-red-100 text-red-700"
                          }`}>
                            {supplier.reg_expiry_year || "N/A"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No suppliers found matching your criteria.</p>
                <p className="text-sm text-slate-400 mt-1">Try adjusting your search filters.</p>
              </div>
            )}

            {/* Pagination */}
            {totalCount > searchParams.limit && (
              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  Showing {searchParams.skip + 1}-{Math.min(searchParams.skip + searchParams.limit, totalCount)} of {totalCount.toLocaleString()}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSearch(Math.max(0, searchParams.skip - searchParams.limit))}
                    disabled={searchParams.skip === 0 || loading}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm text-slate-600 px-2">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSearch(searchParams.skip + searchParams.limit)}
                    disabled={searchParams.skip + searchParams.limit >= totalCount || loading}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Initial State */}
        {!searched && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Search FDA Registered Suppliers</h3>
            <p className="text-slate-500 max-w-md mx-auto mb-6">
              Search the official FDA Medical Device Registration & Listing database to find manufacturers, 
              distributors, and other medical device establishments across all 50 US states.
            </p>
            <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Official FDA Data</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>All 50 States</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Full Addresses</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
